import React from 'react';
import { Brain, Workflow, FileText, Scale, Building, TrendingUp, FileBarChart, ArrowRight, Cpu, Globe2 } from 'lucide-react';
import { services } from '../mock';

const iconMap = {
  Brain: Brain,
  Workflow: Workflow,
  FileText: FileText,
  Scale: Scale,
  Building: Building,
  TrendingUp: TrendingUp,
  FileBarChart: FileBarChart,
  Cpu: Cpu,
  Globe2: Globe2
};

const ServicesSection = () => {
  return (
    <section id="services" className="relative bg-white py-32">
      <div className="max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="mb-20">
          <div className="inline-block bg-brand-blue/10 px-4 py-1.5 rounded-md mb-6">
            <span className="font-mono text-xs tracking-widest text-brand-blue uppercase">Services</span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
            Comprehensive Financial
            <br />
            <span className="text-brand-blue">& Tech Compliance</span> Solutions
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl leading-relaxed">
            From AI/ML compliance to NRI taxation, we provide cutting-edge financial solutions for modern businesses.
          </p>
        </div>

        {/* Bento Grid - Tetris Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isFeatured = service.featured;
            
            // Bento Grid sizing
            let gridClass = "md:col-span-6 lg:col-span-3"; // default medium
            if (service.size === "large") {
              gridClass = "md:col-span-12 lg:col-span-6";
            }

            return (
              <div 
                key={service.id}
                className={`group relative bg-gray-50 border border-gray-200 p-8 hover:border-brand-blue/50 hover:shadow-xl transition-all duration-500 overflow-hidden ${gridClass}`}
                data-testid={`service-card-${service.id}`}
              >
                {/* Tracing Beam Effect for Featured Services */}
                {isFeatured && (
                  <div className="absolute inset-0 overflow-hidden">
                    <div 
                      className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-blue to-transparent animate-beam"
                      style={{
                        animationDelay: `${index * 0.5}s`
                      }}
                    />
                  </div>
                )}

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-14 h-14 flex items-center justify-center mb-6 transition-all duration-500 ${
                    isFeatured 
                      ? 'bg-brand-blue/20 group-hover:bg-brand-blue/30' 
                      : 'bg-gray-200 group-hover:bg-brand-blue/10'
                  }`}>
                    <IconComponent 
                      className={isFeatured ? 'text-brand-blue' : 'text-gray-700 group-hover:text-brand-blue'} 
                      size={28} 
                      strokeWidth={1.5}
                    />
                  </div>

                  {/* Featured Badge */}
                  {isFeatured && (
                    <div className="inline-block mb-4">
                      <span className="font-mono text-[10px] tracking-widest text-brand-blue uppercase bg-brand-blue/10 px-3 py-1">
                        New Service
                      </span>
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-gray-900 mb-4 tracking-tight">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features List with Border Grid Style */}
                  <div className="border-l-2 border-gray-300 pl-4 space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <ArrowRight className="text-brand-blue flex-shrink-0 mt-1" size={16} strokeWidth={2} />
                        <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <button 
                    className="flex items-center gap-2 text-brand-blue font-medium text-sm group-hover:gap-3 transition-all duration-300"
                    data-testid={`service-cta-${service.id}`}
                  >
                    <span>Explore Service</span>
                    <ArrowRight size={16} strokeWidth={2} />
                  </button>
                </div>

                {/* Corner Accent for Featured */}
                {isFeatured && (
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-brand-blue/10 to-transparent" />
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">
            Looking for something specific? We offer customized solutions tailored to your unique needs.
          </p>
          <button 
            className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-hover text-white font-medium px-8 py-3 transition-all duration-300 group"
            data-testid="services-contact-cta"
          >
            <span>Get Custom Solution</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" strokeWidth={2} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
